import {DepotCidSrv, JDepotCidResult, ODepotCidRequest} from "lib/store/cid";
import {JNodeInfos, UrlTreeSrv} from "lib/store/urlTree";
import {JCidMetas} from "lib/store/res";
import {Action, ActionMenu, EButtonUiContext, IAction} from "lib/commons/actions";
import {IReg, IRegPointer, REG} from "lib/commons/registry";
import {ERROR} from "lib/core/errorReport";
import {POPUP} from "back/commons/widgets/popups";
import {JUser, USER} from "lib/core/user";
import {IDepotResEnv, IDepotResUiEnv} from "lib/store/depot";
import {ActionBtn} from "back/commons/widgets/buttons";

export class DepotLcProv {
	static readonly LC_KEY = "lc";

	protected _transitions: Array<DepotLcTransition>;
	protected _states: Array<DepotLcState>;
	protected _cidSrv: DepotCidSrv;
	protected _adminUT: UrlTreeSrv;

	constructor() {
		this._states = [];
		this._transitions = [];
	}

	configure(adminTree: UrlTreeSrv, cidServer: DepotCidSrv) {
		this._cidSrv = cidServer;
		this._adminUT = adminTree;
	}

	async executeTransition(path: string, transitionKey: string): Promise<DepotLcState> {
		const transition = this.getTransition(transitionKey);
		const req: ODepotCidRequest<JCidMetas, JDepotCidResult> = {
			cidMetas: {
				olderPath: path,
				lc: transition.code
			},
			returnProps: ['scCidSessStatus']
		};
		const resp = await this._cidSrv.syncSend(req);
		if (resp.scCidSessStatus == "commited") return transition.to;
		else return null;
	}

	addTransition(transition: DepotLcTransition) {
		const i = this.getTransitionIndex(transition.code);
		if (i == null) this._transitions.push(transition);
		else this._transitions[i] = transition;
	}

	getTransition(key: string): DepotLcTransition {
		const i = this.getTransitionIndex(key);
		return i == null ? null : this._transitions[i];
	}

	getTransitionIndex(key: string): number {
		for (let i = 0; i < this._transitions.length; i++) {
			if (this._transitions[i].code == key) return i;
		}
		return null;
	}

	addState(state: DepotLcState) {
		const i = this.getStateIndex(state.code);
		if (i == null) this._states.push(state);
		else this._states[i] = state;
	}

	getState(key: string): DepotLcState {
		const i = this.getStateIndex(key);
		return i == null ? null : this._states[i];
	}

	getStateIndex(key: string): number {
		for (let i = 0; i < this._states.length; i++) {
			if (this._states[i].code == key) return i;
		}
		return null;
	}

	listAvailableTransitions(state: string, prc: string): Array<DepotLcTransition> {
		let availbleTr = new Array<DepotLcTransition>();
		for (let i = 0; i < this._transitions.length; i++) {
			if (this._transitions[i].isValidTrans(state, prc)) availbleTr.push(this._transitions[i]);
		}
		return availbleTr;
	}
}

export interface ODepotLcTransitionConfig {
	code: string
	label: string
	restrictFromStates: Array<string>
	prcWhiteList: Array<string>
	allowFromAllPrcs: boolean
	to: DepotLcState
	showPerms?: Array<string>
	enablePerms?: Array<string>
	iconUrl?: string
}

export class DepotLcTransition {
	code: string;
	label: string;
	restrictFromStates: Array<string>;
	prcWhiteList: Array<string>;
	allowFromAllPrcs: boolean;
	showPerms?: Array<string>;
	enablePerms?: Array<string>;
	to: DepotLcState;
	iconUrl: string;

	constructor(init: ODepotLcTransitionConfig) {
		this.code = init.code;
		this.label = init.label;
		this.iconUrl = init.iconUrl;
		this.restrictFromStates = init.restrictFromStates || null;
		this.to = init.to;
		this.prcWhiteList = init.prcWhiteList || [];
		this.allowFromAllPrcs = init.allowFromAllPrcs || false;
		this.showPerms = init.showPerms || null;
		this.enablePerms = init.enablePerms || null;
		this.iconUrl = init.iconUrl || null;
	}

	isValidTrans(state: string, prc: string): boolean {
		if (this.restrictFromStates.indexOf(state) === -1) return false;
		if (this.allowFromAllPrcs === false && this.prcWhiteList.indexOf(prc) === -1) return false;
		return true;
	}
}

export class DepotLcState {
	code: string;
	label: string;
	icon: string;

	constructor(code: string, label?: string, icon?: string) {
		this.code = code;
		this.label = label;
		this.icon = icon;
	}
}


/**
 * Menu des transitions exécutables d'une ressource du depot.
 *
 * Action à embarquer en dehors d'un framework Wui Depot
 * (référence explicite à depotlcProv et récup du nodeInfos).
 */
export class DepotResListTransition<C extends IRegPointer<IDepotResEnv>> extends ActionMenu<C> {
	protected _reg: IReg<IDepotResEnv>;

	protected _state: DepotLcState;

	constructor() {
		super('itemListTransition');
		this._description = "Changer d'état...";
	}

	init(reg: IReg<IDepotResEnv>): DepotResListTransition<C> {
		this._reg = reg;
		const lstn = reg.env.nodeInfosChange;
		if (lstn) lstn.on("nodeChange", this.refreshNode.bind(this));
		else reg.env.nodeInfosPending?.then(this.refreshNode.bind(this))
		this.refreshNode();
		return this;
	}

	isEnabled(ctx: C): boolean {
		if (!super.isEnabled(ctx)) return false;
		for (const action of this.getActions()) {
			if (action.isEnabled(ctx)) return true;
		}
		return false;
	}

	refreshNode() {
		const ni = this._reg.env.nodeInfos;
		this._state = ni ? this._reg.env.lifecycle.getState(ni.metas.lc) : null;
		this._label = this._state ? this._state.label : "Chargement en cours...";
		this._icon = this._state ? this._state.icon : null;
	}

	getActions(): IAction<C>[] {
		let list = [];
		if (this._reg.env.nodeInfos == null) return [];
		if (!this._state) return [];
		const transitions = this._reg.env.lifecycle.listAvailableTransitions(this._state.code, this._reg.env.nodeInfos.prc);
		if (transitions != null) for (const tr of transitions.values()) list.push(new ResExecTransition(tr));
		return list;
	}
}

export abstract class DepotResAction<C extends IRegPointer<IDepotResEnv>> extends Action<C> {

	/* Perm appliquée pour évaluer la visibilité de l'action. */
	protected _visResPerms: string[];

	/* Perm appliquée pour évaluer l'activation de l'action. */
	protected _enableResPerms: string[];

	constructor(id: string) {
		super(id);
	}

	isVisible(ctx: C): boolean {
		if (!super.isVisible(ctx)) return false;
		return this.checkResPerms(this._visResPerms, ctx);
	}

	isEnabled(ctx: C): boolean {
		if (!super.isEnabled(ctx)) return false;
		return this.checkResPerms(this._enableResPerms, ctx);
	}

	checkResPerms(perms: string | string[], ctx: C): boolean {
		if (!ctx.reg.env.nodeInfos) return false;
		if (perms && ctx.reg.env.path) {
			return ctx.reg.hasPermission(perms, ctx.reg.env.nodeInfos.roles);
		}
		return true
	}

	requireResVisiblePerm(perms: string | string[]): this {
		if (perms) {
			if (Array.isArray(perms)) {
				for (let i = 0; i < perms.length; i++) this.requireResVisiblePerm(perms[i]);
			} else {
				if (!this._visResPerms) this._visResPerms = [];
				if (this._visResPerms.indexOf(perms) == -1) this._visResPerms.push(perms);
			}
		}
		return this;
	}

	requireResEnabledPerm(perms: string | string[]): this {
		if (perms) {
			if (Array.isArray(perms)) {
				for (let i = 0; i < perms.length; i++) this.requireResEnabledPerm(perms[i]);
			} else {
				if (!this._enableResPerms) this._enableResPerms = [];
				if (this._enableResPerms.indexOf(perms) == -1) this._enableResPerms.push(perms);
			}
		}
		return this;
	}
}

/**
 * Action d'exécution d'une transition
 */
export class ResExecTransition<C extends IRegPointer<IDepotResEnv>> extends DepotResAction<C> {

	constructor(public trans: DepotLcTransition, overrideExec?: (ctx: C, ev?: Event) => any) {
		super(trans.code);
		if (trans.showPerms) this.requireResVisiblePerm(trans.showPerms);
		if (trans.enablePerms) this.requireResEnabledPerm(trans.enablePerms);
		this._label = trans.label;
		this._icon = trans.iconUrl;
		if (overrideExec) this.execute = overrideExec;
	}

	async execute(ctx: C, ev?: Event) {
		const preActions = ctx.reg.mergeLists<(transition: ResExecTransition<C>) => boolean | Promise<boolean>>("lc:transitions:pre:actions", "lc:transitions:" + this._id + ":pre:actions");
		if (preActions) for (const preAction of preActions) {
			const preActionRes = preAction(this);
			const result = preActionRes instanceof Promise ? await preActionRes : preActionRes;
			if (result === false) return;
		}
		const state: DepotLcState = await ctx.reg.env.lifecycle.executeTransition(ctx.reg.env.path, this._id);
		if (state == null) {
			return ERROR.reportError({msg: "Une erreur est survenue durant le changement d'état."});
		}
	}
}

export class ChangeUserResp<C extends IRegPointer<IDepotResUiEnv>> extends DepotResAction<C> {

	constructor(code: string, overrideExec?: (ctx: never, ev?: Event) => any) {
		super(code);
		if (overrideExec) this.execute = overrideExec;
	}

	buildCustomButton(ctx: C, uiContext: EButtonUiContext, parent?: Element): Element | null | undefined {
		return new ChangeUserRespBtn().initialize({
			reg: REG.findReg(parent, ctx),
			action: this,
			actionContext: ctx,
			skin: this.getSkin(ctx),
			skinOver: this.getSkinOver(ctx),
			uiContext: uiContext,
			role: uiContext === 'menu' ? 'menuitem' : 'button'
		});
	}

	async execute(ctx: C, ev?: Event) {
		//const self = DOMSH.findHost<TaskFieldUsers>(this);
		const {UserSelector} = await import("back/core/dialogs/userSelector.js");
		const usersRaw: string[] = ctx.reg.env.nodeInfos.metas[this._id];
		const userSelector = new UserSelector().initialize({
			reg: ctx.reg,
			userGrid: {
				usersSrv: ctx.reg.env.universe.useUsers,
				grid: {selType: "multi", skinOver: "userRespSelector"},
				filterTypeInputVisibility: true,
			},
			selectAndCloseOnDblClick: true,
			startSel: usersRaw != null && usersRaw.length ? await ctx.reg.env.universe.useUsers.getUserSet(usersRaw) : []
		});
		const users = await POPUP.showMenuFromEvent<JUser[]>(userSelector, ev || ctx.reg.env.uiRoot, ctx.reg.env.uiRoot, null, { initWidth: '20em'}).onNextClose();
		if (users) {
			let usersAr = [];
			for (const user of users) usersAr.push(user.account);
			const req: ODepotCidRequest<JCidMetas, JDepotCidResult> = {
				cidMetas: {
					olderPath: ctx.reg.env.path,
				},
				returnProps: ['scCidSessStatus']
			};
			req.cidMetas[this._id] = JSON.stringify(usersAr);
			const resp = await ctx.reg.env.universe.cid.syncSend(req);
			if (resp.scCidSessStatus !== "commited") return ERROR.reportError({msg: "Une erreur est survenue durant le changement de responsabilité."});
		}
	}
}

REG.reg.registerSkin('userRespSelector', 1, /* language=CSS */`
.inSel {
	background-color: var(--row-inSel-bgcolor);
}
`);


class ChangeUserRespBtn extends ActionBtn<IRegPointer<IDepotResUiEnv>> {
	_hideLabel = false;

	connectedCallback() {
		if (this.actionContext.reg.env.nodeInfos !== null) this.onResChange(this.actionContext.reg.env.nodeInfos);
		super.connectedCallback();
		const lstn = this.actionContext.reg.env.nodeInfosChange;
		if (lstn) lstn.on("nodeChange", (this._onResChange = this.onResChange.bind(this)));
		else this.actionContext.reg.env.nodeInfosPending?.then((ni: JNodeInfos) => {this.onResChange(ni)});
	}

	disconnectedCallback() {
		if (this._onResChange) {
			this.actionContext.reg.env.nodeInfosChange.removeListener("nodeChange", this._onResChange);
			this._onResChange = null;
		}
	}

	async onResChange(niNew: JNodeInfos) {
		if (niNew) {
			let label = this.action.getLabel(this.actionContext);
			const users = niNew.metas[this.action.getId()];
			if (users && users.length > 0) {
				label += USER.getPrimaryName(await this.actionContext.reg.env.universe.useUsers.getUser(users[0]));
				if (users.length > 1) label += "...";
			} else label = "Aucun";
			this.label = label;
		} else {
			this.label = "";
		}
	}

	protected _onResChange: (niNew: JNodeInfos, niOld: JNodeInfos) => void;
}

customElements.define('store-resp-changeuser', ChangeUserRespBtn);